"use client";

import { cn } from "@universe/ui/lib/utils";
import React from "react";

/**
 * Viewport `top` where a level-1 sticky band pins when the breadcrumb page
 * header is present (topValues[1] below). Exported so layouts that stack
 * further sticky bands below a level-1 band (the account page's
 * currency-dropdown band) can derive those bands' pin-tops from the same
 * anchor instead of duplicating the constant.
 */
export const SUBHEADER_LEVEL1_TOP = 63;

interface StickyHeaderProps {
  className?: string;
  subHeader?: number;
  noPageHeader?: boolean;
  fullWidth?: boolean;
  style?: React.CSSProperties;
}

const StickyHeader = React.forwardRef<
  HTMLDivElement,
  WithChildren<StickyHeaderProps>
>(function StickyHeader(
  {
    className,
    children,
    subHeader = 1,
    noPageHeader = false,
    fullWidth = false,
    style,
  },
  ref
) {
  const topValues: Record<number, number> = {
    1: noPageHeader ? 33 : SUBHEADER_LEVEL1_TOP,
    2: noPageHeader ? 64 : 99,
    3: noPageHeader ? 109 : 144,
    // 4 (account tabs band) is a first-frame fallback only — the real pin-top
    // is measurement-driven (DashboardHeaderProvider `tabsTop`, passed via
    // `style`), because the dropdown band above it is taller than 94-63px.
    4: noPageHeader ? 94 : 94,
  };

  const baseTop = topValues[subHeader] ?? topValues[1];

  return (
    <div
      ref={ref}
      className={cn(
        // Solid white at every level. These bands used to fade out towards
        // their bottom edge (level 1 to white/50, level 2+ to white/70) over a
        // backdrop blur, which let the list scrolling underneath ghost through
        // — visible as half-rendered rows and amounts sitting behind the
        // header. Four call sites had already patched around it individually
        // with `!bg-white`; the fill belongs here so every band matches and no
        // caller has to override it. No backdrop blur: there is nothing to see
        // through an opaque fill.
        "sticky z-3 px-4 lg:px-7 mx-[-16px] lg:mx-[-28px] bg-white",
        fullWidth && "w-full right-0 -mx-0 lg:mx-[-1px] px-0 lg:px-0",
        // Level 2+ stacks below the level-1 band.
        subHeader > 1 && "z-2",
        className
      )}
      style={{
        top: `calc(${baseTop}px + var(--s21-banner-height, 0px))`,
        width: fullWidth ? "100%" : "auto",
        ...style,
      }}
    >
      <div
        className={cn(fullWidth && "w-full", className || "")}
        style={{ width: fullWidth ? "100%" : "auto" }}
      >
        {children}
      </div>
    </div>
  );
});

export default StickyHeader;
